/**
 * Die Siegbedingung — zwei Varianten, die sich ausschließen (§26, §42).
 *
 * Der Host wählt sie bei der Partieerstellung, und **die Variante bestimmt die Uhr**
 * (`docs/OPEN_QUESTIONS.md` Nr. 22):
 *
 * | Variante | Bedenkzeit | Punktziel |
 * |---|---|---|
 * | `POINTS` — A | **keine** | `scoring.target` aktiv |
 * | `TIME` — B | **erforderlich** | inaktiv |
 *
 * Weil es in Variante A keine Uhr gibt, kann dort niemand ausscheiden: die Zeitregeln unten
 * sind ausschließlich Variante B. Umgekehrt ist in Variante B das Punktziel wirkungslos — eine
 * Seite darf beliebig viele Punkte sammeln, ohne die Partie zu beenden.
 *
 * **Gerechnet wird in Seiten, nie in Spielern.** Eine Seite ist ein Team oder ein einzelner
 * Spieler (`sideOf` in `game-state.ts`). Nur so gilt derselbe Code für 1:1, FFA und alle
 * Teammodi, auch ungleich besetzte (Nr. 24).
 *
 * **Wann zu prüfen ist:** nach jeder Punktevergabe und nach jedem Ausscheiden. Im
 * Commit-Ablauf (`execute-action.ts`) steht `applyVictory` deshalb am Ende.
 */
import { isEliminated, scoreOf, setStatus } from './game-state.js';
function sideOfPlayer(player) {
    return player.team ?? player.id;
}
/**
 * Die Seiten dieser Partie, in Zugreihenfolge ihres ersten Auftretens.
 *
 * Die Reihenfolge ist deterministisch, damit auch Gleichstandsauflösungen reproduzierbar
 * bleiben (§128).
 */
export function sidesOf(state) {
    const seen = new Set();
    const sides = [];
    for (const id of state.turnOrder) {
        const player = state.players.find((entry) => entry.id === id);
        if (player === undefined)
            continue;
        const side = sideOfPlayer(player);
        if (seen.has(side))
            continue;
        seen.add(side);
        sides.push(side);
    }
    return sides;
}
function playersOfSide(state, side) {
    return state.players.filter((player) => sideOfPlayer(player) === side);
}
/** Die Punkte einer Seite: in Teamspielen die Summe ihrer Mitglieder (§23). */
function scoreOfSide(state, side) {
    return playersOfSide(state, side).reduce((sum, player) => sum + scoreOf(state, player.id), 0);
}
/**
 * Hat diese Seite noch Bedenkzeit?
 *
 * Ein Team ist erst draußen, wenn **alle** Mitglieder ausgeschieden sind — ein Timeout trifft
 * nur den betroffenen Spieler, das Team spielt mit den übrigen weiter (Nr. 3). Aus einem 3:3
 * wird so ein 2:3.
 */
function sideHasTime(state, side) {
    return playersOfSide(state, side).some((player) => !isEliminated(state, player.id));
}
/** Variante A: eine Seite erreicht das Punktziel aus `scoring.target` (§26). */
function checkPointTarget(state) {
    const target = state.ruleConfig.scoring.target;
    const reached = sidesOf(state).filter((side) => scoreOfSide(state, side) >= target);
    if (reached.length === 0)
        return undefined;
    // Regulär kann nur eine Seite das Ziel überschreiten: es punktet immer genau eine Seite
    // pro Zug, und direkt danach wird geprüft. Die Auswahl der höchsten Punktzahl ist nur eine
    // Absicherung für den Fall, dass die Prüfung einmal übersprungen wurde.
    const winner = reached.reduce((best, side) => scoreOfSide(state, side) > scoreOfSide(state, best) ? side : best);
    return { winner, reason: 'POINT_TARGET' };
}
/**
 * Variante B: **Zeit gewinnt, Punkte schützen vor der Niederlage** (§26, §42, Nr. 5).
 *
 * Die Partie endet, sobald **höchstens eine** Seite noch Zeit hat. Dann gilt:
 *
 * | Lage der Seite mit Zeit | Ergebnis |
 * |---|---|
 * | sie führt oder liegt gleich | **sie gewinnt** (`LAST_WITH_TIME`) |
 * | sie liegt punktemäßig hinten | **Unentschieden** (`LEADER_OUT_OF_TIME`, `winner: null`) |
 *
 * **Warum das Unentschieden** (Entscheidung vom 2026-10-07, revidiert Nr. 5 vom 2026-10-02):
 * vorher gewann die Seite mit Zeit auch mit 0 gegen 7. Damit war „schnell ziehen und nie
 * punkten" eine gültige Strategie — das Brettspiel entschied in Variante B gar nichts mehr.
 * Jetzt kommt der Schnellspieler gegen einen Punktenden höchstens noch zum Remis, und um zu
 * gewinnen muss er selbst spielen.
 *
 * **Warum nicht „die Punkte entscheiden".** Weil dann, wer führt, nur noch die eigene Uhr
 * leerlaufen lassen müsste: Nichtstun wäre ab der ersten Führung die beste Strategie. Ein
 * Zeitablauf darf also nie zum Vorteil dessen sein, dem die Zeit ausgeht — deshalb ist das
 * Unentschieden sein **bestmögliches** Ergebnis und nicht ein Sieg.
 *
 * **„Führt" heißt: die meisten Punkte aller Seiten**, auch der ausgeschiedenen, und in Teams
 * die **Summe** der Seite (§23). Eine überlebende Seite, die hinter einer längst
 * ausgeschiedenen liegt, gewinnt also nicht.
 *
 * **Der Fall „keine Seite hat mehr Zeit" ist unmöglich** und hat deshalb keine Regel. Es
 * läuft immer nur die Uhr des Spielers am Zug (§38), pro Ablauf scheidet also genau einer
 * aus — und nach **jedem** Ausscheiden läuft diese Prüfung und beendet die Partie, sobald
 * eine Seite übrig ist. Der Zustand müsste nach dem Partieende entstehen, und dort greift
 * weder der Uhrenschlag (`applyClockExpiry` verlangt `PLAYING`) noch ein Debug-Eingriff
 * (Nr. 41). Bis Zyklus R1 standen hier zwei weitere Prüfungen — `MOST_POINTS` und
 * `LONGEST_SURVIVED` — für genau diesen Fall; sie waren nie erreichbar.
 */
function checkTimeVictory(state) {
    const sides = sidesOf(state);
    // Eine Partie mit nur einer Seite wäre sonst von Anfang an entschieden. Entarteter Fall,
    // regulär unmöglich (§8 verlangt mindestens zwei Seiten), über den Debug-Modus erreichbar.
    if (sides.length < 2)
        return undefined;
    const withTime = sides.filter((side) => sideHasTime(state, side));
    /*
      `!== 1` und nicht `> 1`: „keine Seite hat Zeit" ist nach dem Modulkopf unmöglich, und
      diese Zeile ist keine Regel für diesen Fall, sondern eine **Totalitätsbremse**. Sie sagt
      „noch nicht entschieden", weil `checkVictory` eine reine Abfrage ist — sie wird auch zum
      Vorausrechnen in der Simulation benutzt und darf auf keinem darstellbaren Zustand werfen.
    */
    if (withTime.length !== 1)
        return undefined;
    const survivor = withTime[0];
    const best = Math.max(...sides.map((side) => scoreOfSide(state, side)));
    return scoreOfSide(state, survivor) >= best
        ? { winner: survivor, reason: 'LAST_WITH_TIME' }
        : { winner: null, reason: 'LEADER_OUT_OF_TIME' };
}
/**
 * Ist die Partie entschieden? Dann das Ergebnis, sonst `undefined`.
 *
 * Reine Abfrage — sie ändert nichts und lässt sich auch benutzen, um eine Partie als „fast
 * entschieden" anzuzeigen oder sie in der Simulation vorauszurechnen.
 */
export function checkVictory(state) {
    return state.victory === 'POINTS' ? checkPointTarget(state) : checkTimeVictory(state);
}
/**
 * Beendet die Partie, wenn sie entschieden ist — Status `FINISHED` und das Ergebnis im
 * Zustand. Ist sie nicht entschieden, bleibt alles unverändert.
 */
export function applyVictory(state) {
    if (state.status !== 'PLAYING') {
        throw new Error(`Die Siegbedingung gehört in den Status PLAYING, nicht in ${state.status} (§47)`);
    }
    const outcome = checkVictory(state);
    if (outcome === undefined)
        return state;
    return { ...setStatus(state, 'FINISHED'), outcome };
}
//# sourceMappingURL=victory.js.map